import type { ReactNode } from 'react';
import { initialsOf } from '../lib/format';
import styles from './Avatar.module.css';

export type AvatarSize = 'sm' | 'md' | 'lg';

interface AvatarProps {
  readonly name: string;
  readonly size?: AvatarSize;
  /**
   * 06-qol §12: every automated action is labelled as such, and system-actor
   * events display differently from human ones, everywhere.
   */
  readonly isSystem?: boolean;
}

export function Avatar({ name, size = 'md', isSystem = false }: AvatarProps): ReactNode {
  const classes = [
    styles.avatar,
    size !== 'md' ? styles[size] : '',
    isSystem ? styles.system : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} title={name} aria-hidden>
      {isSystem ? 'SY' : initialsOf(name)}
    </span>
  );
}

interface AvatarStackProps {
  readonly names: readonly string[];
  readonly max?: number;
  readonly size?: AvatarSize;
}

/** An interview panel, a set of approvers — several people in the width of two. */
export function AvatarStack({ names, max = 3, size = 'sm' }: AvatarStackProps): ReactNode {
  const shown = names.slice(0, max);
  const hidden = names.length - shown.length;

  return (
    <span className={styles.stack} title={names.join(', ')}>
      {shown.map((name) => (
        <Avatar key={name} name={name} size={size} />
      ))}
      {hidden > 0 && (
        <span
          className={`${styles.avatar} ${size !== 'md' ? styles[size] : ''} ${styles.overflow}`}
        >
          +{hidden}
        </span>
      )}
    </span>
  );
}
